import React, { useCallback, useMemo } from 'react';
import {
  BarChartPro,
  ChartsReferenceLine,
  FadeOptions,
  HighlightOptions,
  LineChartPro,
  LineSeriesType,
  PieChart,
  PieValueType,
  labelMarkClasses,
  legendClasses,
  lineElementClasses,
} from '@mui/x-charts-pro';
import {
  ISammanfattningRows,
  ISammanfattningsdata,
} from '../../EkonomiskAnalysSammanfattning';
import { Theme, useTheme } from '@mui/material/styles';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import { getCurrencyFormatterParams } from '../../../../Utils/paramUtils';
import {
  useIsBreakpoint,
  useIsBreakpointOrLarger,
  useIsBreakpointOrSmaller,
} from '../../../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import {
  AnalysLevelEnum,
  AnalysTypeEnum,
  BUDGET_RIKTVARDE_LABEL,
  PrevColumnTypeEnum,
  RadioBarChartSortByOptionsEnum,
  SpecifikationTypeEnum,
  UTGAENDE_VARDE_LABEL,
  UTGAENDE_VARDE_LAST_YEAR_LABEL,
} from '../../../../Const/EkonomiskAnalysConsts';
import {
  capitalizeFirstLetter,
  getMonthText,
} from '../../../../../../../../../Utils/dateUtils';
import { IEkonomiskAnalysFilledDates } from '../../../AnalysSettingsHandler/AnalysSettingsHandler';
import {
  getBarChartComparator,
  getLineChartLabel,
  getPercentageText,
  getPieChartLabel,
  getKontodelString,
  getSumOfPrevPeriodVarde,
  getPrevPeriodsUtgaendeVarde,
  ZoomInteractionConfig,
} from '../../../../Utils/detaljanalysUtils';
import { IColumn } from '../../../EkonomiskAnalysTable/EkonomiskAnalysTableBase';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import { EkonomiskAnalysCustomAxisTooltip } from './Components/EkonomiskAnalysCustomAxisTooltip';
import { EkonomiskAnalysCustomItemTooltip } from './Components/EkonomiskAnalysCustomItemTooltip';
import EkonomiskAnalysLegendLine from './Components/EkonomiskAnalysLegendLine';
import EDRadioGroup, {
  IRadioDef,
} from '../../../../../../../../../BaseComponents/RadioGroup/EDRadioGroup';
import mergeSort from '../../../../../../../../../Utils/mergeSort';
import { EDControlledExpansionPanel } from '../../../../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import EkonomiskAnalysExpansionLabel from './Components/EkonomiskAnalysExpansionLabel';
import { IPrintSections } from '../../DetaljanalysPrintModal';
import EkonomiskAnalysResultatChart from './Components/EkonomiskAnalysResultatChart';
import { ISelection } from '../../../../Queries/EkonomiskAnalysListQuery/EkonomiskAnalysListQuery';
import { ISumData } from '../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { IUIUrval } from '../../../../../../../../../Types/UrvalTypes';

// Local replacement for internal helper:
type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

interface IProps {
  analysData: ISammanfattningsdata;
  analysLevel: AnalysLevelEnum;
  analysType: AnalysTypeEnum;
  availableReports: ISelection[];
  currentKoddel: string | undefined;
  dates: IEkonomiskAnalysFilledDates;
  denomination: number;
  expandedBarChart: boolean;
  expandedLineChart: boolean;
  expandedPieChart: boolean;
  expandedResultatDiagram: boolean;
  nrOfDecimals: number;
  rubrikNiva: string;
  selectedAnalysCodeprivilegeID: string;
  selectedRowHeader: string | undefined;
  subLevelNiva: string | undefined;
  sums: ISumData[];
  kolumnerRubriker: IColumn[];
  setExpandedBarChart: (value: boolean) => void;
  setExpandedLineChart: (value: boolean) => void;
  setExpandedPieChart: (value: boolean) => void;
  setExpandedResultatDiagram: (value: boolean) => void;
  printSections: IPrintSections;
  firstVisibleSection: string | null;
  showFordelningDiagram: boolean;
  showResultatDiagram: boolean;
  urval: IUIUrval | null;
}

interface ILineSeriesItem {
  id: number;
  utgaendeVarde: number | null;
  projectedUtgaendeVarde: number | null;
  budgetArVarde: number;
  utgaendeVardeLastYear: number;
  [key: string]: string | number | null | undefined;
}

const currFormatter = findFormatter('TEDFormatCurr');

const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const zoomConfig: ZoomInteractionConfig = {
  zoom: [{ type: 'wheel', requiredKeys: ['Alt'] }],
};

const EkonomiskAnalysSammanfattningCharts: React.FC<IProps> = props => {
  const isSmallScreen = useIsBreakpointOrSmaller('sm');
  const isMediumScreen = useIsBreakpoint('md');
  const isLargeScreen = useIsBreakpointOrLarger('lg');
  const theme = useTheme<Theme>();
  const [sortByBarChartData, setSortByBarChartData] = React.useState<string>(
    RadioBarChartSortByOptionsEnum.BENAMNING
  );

  const enhet = props.analysData?.rapportInfo?.enhet || 'kr';
  const monthFullText = useMemo(
    () => capitalizeFirstLetter(getMonthText(props.dates.from.month)),
    [props.dates.from.month]
  );
  const kostnadsData: ISammanfattningRows[] =
    props.analysData?.kostnadsData?.nextToLastRow != undefined
      ? props.analysData.kostnadsData.nextToLastRow
      : props.analysData.kostnadsData.lastRow;

  const gridData: ISammanfattningRows[] = useMemo(() => {
    return kostnadsData.map(row => {
      const headerValue = getKontodelString(row.header);

      return {
        ...row,
        header: headerValue,
        utgaendeVarde: parseFloat(
          row.utgaendeVarde?.toString() === '' || row.utgaendeVarde == undefined
            ? '0'
            : row.utgaendeVarde?.toString()
        ),
        utgaendeVardeLastYear: parseFloat(
          row.utgaendeVardeLastYear?.toString() === '' ||
            row.utgaendeVardeLastYear == undefined
            ? '0'
            : row.utgaendeVardeLastYear?.toString()
        ),
        utgaendeVardePeriodLastYear: parseFloat(
          row.utgaendeVardePeriodLastYear?.toString() === '' ||
            row.utgaendeVardePeriodLastYear == undefined
            ? '0'
            : row.utgaendeVardePeriodLastYear?.toString()
        ),
        budgetArVarde: parseFloat(
          row.budgetArVarde?.toString() === '' || row.budgetArVarde == undefined
            ? '0'
            : row.budgetArVarde?.toString()
        ),
      };
    });
  }, [kostnadsData]);

  const lineChartSumData = gridData.reduce<{
    utgaendeVarde: number;
    budgetArVarde: number;
    budgetRiktvarde: number;
    utgaendeVardeLastYear: number;
    utgaendeVardePeriodLastYear: number;
    [key: string]: number;
  }>(
    //Gå igenom alla rader i rapporten och summera värderna för varje kolumn
    (accumulatedValues, currentRow) => {
      const sumOfPrevPeriodsUtgaendeVarde = getSumOfPrevPeriodVarde(
        currentRow,
        props.kolumnerRubriker,
        SpecifikationTypeEnum.UNDEFINED
      );
      const visibleColumnValues = {
        utgaendeVarde:
          accumulatedValues.utgaendeVarde + currentRow.utgaendeVarde,
        budgetRiktvarde:
          accumulatedValues.budgetRiktvarde + currentRow.budgetRiktvarde,
        budgetArVarde:
          accumulatedValues.budgetArVarde + currentRow.budgetArVarde,
        utgaendeVardeLastYear:
          accumulatedValues.utgaendeVardeLastYear +
          currentRow.utgaendeVardeLastYear,
        utgaendeVardePeriodLastYear:
          accumulatedValues.utgaendeVardePeriodLastYear +
          currentRow.utgaendeVardePeriodLastYear,
      };

      // Add values from previous periods
      const prevPeriodKeys = Object.keys(sumOfPrevPeriodsUtgaendeVarde);
      const updatedPrevPeriodValues: { [key: string]: number } = {};
      prevPeriodKeys.forEach(prevPeriodKey => {
        const prevPeriodValue = accumulatedValues[prevPeriodKey];
        if (prevPeriodValue) {
          updatedPrevPeriodValues[prevPeriodKey] =
            prevPeriodValue + sumOfPrevPeriodsUtgaendeVarde[prevPeriodKey];
        } else {
          updatedPrevPeriodValues[prevPeriodKey] =
            sumOfPrevPeriodsUtgaendeVarde[prevPeriodKey];
        }
      });

      return { ...visibleColumnValues, ...updatedPrevPeriodValues };
    },
    {
      utgaendeVarde: 0,
      budgetArVarde: 0,
      budgetRiktvarde: 0,
      utgaendeVardeLastYear: 0,
      utgaendeVardePeriodLastYear: 0,
    }
  );

  const lineSeries = useMemo<ILineSeriesItem[]>(
    () =>
      months.map(month => {
        if (month === 0) {
          //Första punkten i origo
          return {
            id: month,
            utgaendeVarde: 0,
            projectedUtgaendeVarde: 0,
            budgetRiktvarde: 0,
            budgetArVarde: 0,
            utgaendeVardeLastYear: 0,
          };
        }
        if (props.dates.from.month < month) {
          // Datan för den projicerade delen av året

          const prevYearPeriodValue = getPrevPeriodsUtgaendeVarde(
            month,
            lineChartSumData,
            PrevColumnTypeEnum.PREVIOUS_YEAR
          );

          return {
            id: month,
            utgaendeVarde: null,
            projectedUtgaendeVarde:
              (lineChartSumData.utgaendeVarde / props.dates.from.month) * month,
            budgetArVarde: (lineChartSumData.budgetArVarde / 12) * month,
            utgaendeVardeLastYear: prevYearPeriodValue,
          };
        }

        if (props.dates.from.month === month) {
          // Sista punkten med faktisk data

          const prevYearPeriodValue = getPrevPeriodsUtgaendeVarde(
            month,
            lineChartSumData,
            PrevColumnTypeEnum.PREVIOUS_YEAR
          );

          return {
            id: month,
            utgaendeVarde: lineChartSumData.utgaendeVarde,
            projectedUtgaendeVarde: lineChartSumData.utgaendeVarde,
            utgaendeVardeLastYear: prevYearPeriodValue,
            budgetArVarde: (lineChartSumData.budgetArVarde / 12) * month,
          };
        } else {
          //Punkte från förågende perioder
          const currentYearPrevPeriodValue = getPrevPeriodsUtgaendeVarde(
            month,
            lineChartSumData,
            PrevColumnTypeEnum.CURRENT_YEAR
          );

          const prevYearPeriodValue = getPrevPeriodsUtgaendeVarde(
            month,
            lineChartSumData,
            PrevColumnTypeEnum.PREVIOUS_YEAR
          );

          return {
            id: month,
            utgaendeVarde: currentYearPrevPeriodValue,
            projectedUtgaendeVarde: null,
            budgetArVarde: (lineChartSumData.budgetArVarde / 12) * month,
            utgaendeVardeLastYear: prevYearPeriodValue,
          };
        }
      }),
    [props.dates.from.month, lineChartSumData]
  );

  const valueFormatterCallback = useCallback(
    (value: number | null) => {
      if (value === null) {
        return '';
      }

      const stringValue = `${currFormatter.displayer.display(
        value ?? 0,
        true,
        getCurrencyFormatterParams(
          { fieldInfo: [] },
          props.denomination,
          0,
          'Y'
        )
      )} ${enhet}`;
      return value === 0 ? `${value} ${enhet}` : stringValue;
    },
    [enhet, props.denomination]
  );

  const barChartSeries = useMemo(
    () => [
      {
        color: theme.customProperties.customDetaljanalys.deepBlue,
        dataKey: 'utgaendeVarde',
        highlightScope: { highlight: 'item' as HighlightOptions },
        label: UTGAENDE_VARDE_LABEL,
        valueFormatter: valueFormatterCallback,
      },
      {
        color: theme.customProperties.dustyRainbow.amber,
        dataKey: 'budgetRiktvarde',
        highlightScope: { highlight: 'item' as HighlightOptions },
        label: BUDGET_RIKTVARDE_LABEL,
        valueFormatter: valueFormatterCallback,
      },
      {
        color: theme.customProperties.dustyRainbow.hyacinth,
        dataKey: 'utgaendeVardePeriodLastYear',
        highlightScope: { highlight: 'item' as HighlightOptions },
        label: UTGAENDE_VARDE_LAST_YEAR_LABEL,
        valueFormatter: valueFormatterCallback,
      },
    ],
    [
      theme.customProperties.dustyRainbow.amber,
      theme.customProperties.dustyRainbow.hyacinth,
      theme.customProperties.customDetaljanalys.deepBlue,
      valueFormatterCallback,
    ]
  );

  const lineChartSeries = useMemo(
    () =>
      [
        {
          dataKey: 'utgaendeVarde',
          label: UTGAENDE_VARDE_LABEL,
          valueFormatter: valueFormatterCallback,
          highlightScope: {
            highlight: 'series' as HighlightOptions,
            fade: 'global' as FadeOptions,
          },
          color: theme.customProperties.customDetaljanalys.deepBlue,
          id: 'utgaendeVarde',
          curve: 'linear',
        },
        {
          dataKey: 'projectedUtgaendeVarde',
          label: 'Utgående värde prognos',
          valueFormatter: valueFormatterCallback,
          highlightScope: {
            highlight: 'series' as HighlightOptions,
            fade: 'global' as FadeOptions,
          },
          color: theme.customProperties.customDetaljanalys.deepBlue,
          connectNulls: false,
          labelMarkType: EkonomiskAnalysLegendLine,
          id: 'projectedUtgaendeVarde',
          curve: 'linear',
        },
        {
          dataKey: 'budgetArVarde',
          label: BUDGET_RIKTVARDE_LABEL,
          valueFormatter: valueFormatterCallback,
          highlightScope: {
            highlight: 'series' as HighlightOptions,
            fade: 'global' as FadeOptions,
          },
          color: theme.customProperties.dustyRainbow.amber,
          id: 'budgetRiktvarde',
          curve: 'linear',
        },
        {
          dataKey: 'utgaendeVardeLastYear',
          label: UTGAENDE_VARDE_LAST_YEAR_LABEL,
          valueFormatter: valueFormatterCallback,
          highlightScope: {
            highlight: 'series' as HighlightOptions,
            fade: 'global' as FadeOptions,
          },
          color: theme.customProperties.dustyRainbow.hyacinth,
          id: 'utgaendeVardeLastYear',
          curve: 'linear',
        },
      ] as MakeOptional<LineSeriesType, 'type'>[],
    [
      theme.customProperties.dustyRainbow.amber,
      theme.customProperties.dustyRainbow.hyacinth,
      theme.customProperties.customDetaljanalys.deepBlue,
      valueFormatterCallback,
    ]
  );

  const totalAmount = useMemo(
    () => gridData.reduce((acc, curr) => acc + Math.abs(curr.utgaendeVarde), 0),
    [gridData]
  );

  const pieChartData: MakeOptional<PieValueType, 'id'>[] = useMemo(
    () =>
      gridData.map(row => {
        const utgaendeVarde = Math.abs(row.utgaendeVarde);
        const percentage =
          totalAmount > 0 ? (utgaendeVarde / totalAmount) * 100 : 0;

        const headerValue = getKontodelString(row.header);
        const percentageText = getPercentageText(percentage);

        const rowValueText = valueFormatterCallback(utgaendeVarde);

        return {
          id: headerValue,
          value: percentage,
          subLabel: rowValueText,
          label: (location: string) => {
            if (location === 'tooltip' || location === 'arc') {
              return headerValue;
            } else {
              if (percentage < 1) {
                return headerValue + ' - ' + percentageText;
              }
              return headerValue;
            }
          },
        };
      }),
    [gridData, totalAmount, valueFormatterCallback]
  );

  const barChartData = useMemo(() => {
    const comparator = getBarChartComparator(sortByBarChartData);

    // sort the referenced array in-place (sortedData references gridData)
    const sortedData = mergeSort(gridData, comparator);

    const rowWithHeader = sortedData.map(row => {
      const headerValue = getKontodelString(row.header);
      return {
        ...row,
        header: headerValue,
      };
    });

    //Remove prevPeriods before returning to satisfy BarChartPro dataset type
    return rowWithHeader.map(({ prevPeriods, ...rest }) => rest);
  }, [sortByBarChartData, gridData]);

  const radioBarChartDefs: IRadioDef[] = useMemo(() => {
    return [
      {
        value: RadioBarChartSortByOptionsEnum.BENAMNING,
        label: 'Alfanumerisk',
      },
      {
        value: RadioBarChartSortByOptionsEnum.UTFALLDESC,
        label: 'Utgående \u2193 (fallande)',
      },
      {
        value: RadioBarChartSortByOptionsEnum.UTFALLASC,
        label: 'Utgående \u2191 (stigande)',
      },
      {
        value: RadioBarChartSortByOptionsEnum.BUDGETDESC,
        label: 'Budget \u2193 (fallande)',
      },
      {
        value: RadioBarChartSortByOptionsEnum.BUDGETASC,
        label: 'Budget \u2191 (stigande)',
      },
    ];
  }, []);

  const onRadioBarChartBtnChange = useCallback((newValue: string) => {
    setSortByBarChartData(newValue);
  }, []);

  return (
    <EDBox
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexFlow: 'column',
        boxSizing: 'border-box',
        gap: 1,
        width: '100%',
        minWidth: 0,
        '@media print': {
          minWidth: 0,
          maxWidth: '100%',
          width: '100%',
          margin: 0,
          padding: 0,

          '& svg': {
            maxWidth: '100%',
            width: '100%',
            height: 'auto',
          },
        },
      }}
    >
      {props.showResultatDiagram && (
        <EDBox
          sx={{
            width: '100%',
            '@media print': {
              minWidth: 0,
              maxWidth: '100%',
              width: '100%',
            },
          }}
          className={`print-section ${
            !props.printSections.resultatdiagram ? 'print-hidden' : ''
          } ${
            props.firstVisibleSection === 'resultatdiagram'
              ? 'first-section'
              : ''
          }`}
          data-print-section="resultatdiagram"
        >
          <EDControlledExpansionPanel
            borderVisible
            headerColor={theme.customProperties.MenuList.baseBg}
            expanded={props.expandedResultatDiagram}
            expandIconColor="primary"
            label={
              <EkonomiskAnalysExpansionLabel
                labelText={`Utgående resultat per period`}
                iconType="chart_bar"
                iconVerticalAlign="sub"
              />
            }
            onChange={(_, newValue) =>
              props.setExpandedResultatDiagram(newValue)
            }
            isCompact
          >
            <EDBox
              sx={{
                width: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <EDTypography
                variant="h6"
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexFlow: 'column',
                  justifyContent: 'center',
                  '@media print': {
                    display: 'none',
                  },
                }}
              >
                <EDTypography fontSize={10} p={0} m={0}>
                  Zoom: Alt + scroll
                </EDTypography>
              </EDTypography>
              <EkonomiskAnalysResultatChart
                analysLevel={props.analysLevel}
                analysType={props.analysType}
                availableReports={props.availableReports}
                currentKoddel={props.currentKoddel}
                dates={props.dates}
                rows={kostnadsData}
                rubrikNiva={props.rubrikNiva}
                selectedAnalysCodeprivilegeID={
                  props.selectedAnalysCodeprivilegeID
                }
                selectedRowHeader={props.selectedRowHeader}
                sums={props.sums}
                urval={props.urval}
                valueFormatterCallback={valueFormatterCallback}
                zoomConfig={zoomConfig}
              />
            </EDBox>
          </EDControlledExpansionPanel>
        </EDBox>
      )}

      <EDBox
        sx={{
          width: '100%',
          '@media print': {
            minWidth: 0,
            maxWidth: '100%',
            width: '100%',
          },
        }}
        className={`print-section ${
          !props.printSections.linjediagram ? 'print-hidden' : ''
        } ${
          props.firstVisibleSection === 'linjediagram' ? 'first-section' : ''
        }`}
        data-print-section="linjediagram"
      >
        <EDControlledExpansionPanel
          borderVisible
          headerColor={theme.customProperties.MenuList.baseBg}
          expanded={props.expandedLineChart}
          expandIconColor="primary"
          label={
            <EkonomiskAnalysExpansionLabel
              labelText={`${getLineChartLabel(props.analysType)}`}
              subLabelText={`${monthFullText} ${props.dates.from.year}`}
              iconType="line_chart"
              iconVerticalAlign="sub"
            />
          }
          onChange={(_, newValue) => props.setExpandedLineChart(newValue)}
          isCompact
        >
          <EDBox
            sx={{
              width: '100%',
              minWidth: 0,
              overflowX: 'hidden',
              display: 'flex',
              flexFlow: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <EDTypography
              variant="h6"
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                '@media print': {
                  display: 'none',
                },
              }}
            >
              <EDTypography fontSize={10} p={0} m={0}>
                Zoom: Alt + scroll
              </EDTypography>
            </EDTypography>

            <LineChartPro
              axisHighlight={{ y: 'line', x: 'line' }}
              dataset={lineSeries}
              grid={{ horizontal: true }}
              height={600}
              sx={{
                width: '100%',
                '& .MuiLineElement-root': {
                  strokeWidth: 4,
                },
                '& .MuiLineElement-series-projectedUtgaendeVarde': {
                  strokeDasharray: '10 5',
                  strokeWidth: 4,
                },
                [`.${lineElementClasses.root}[data-series="projectedUtgaendeVarde"], .${legendClasses.item}[data-series="projectedUtgaendeVarde"] .${labelMarkClasses.fill}`]:
                  {
                    strokeDasharray: '10 5',
                  },

                [`.${legendClasses.mark}`]: {
                  width: 24,
                },
              }}
              series={lineChartSeries}
              slotProps={{
                tooltip: { trigger: 'axis' },
              }}
              slots={{
                tooltip: tooltipProps => (
                  <EkonomiskAnalysCustomAxisTooltip
                    {...tooltipProps}
                    currencyValues
                    currentMonth={props.dates.from.month}
                  />
                ),
              }}
              xAxis={[
                {
                  data: months,
                  tickInterval: months,
                  valueFormatter: (
                    value: number,
                    context: { location: string }
                  ) => {
                    if (value === 0) {
                      return '';
                    }
                    if (context.location === 'tooltip') {
                      return capitalizeFirstLetter(getMonthText(value));
                    }

                    return capitalizeFirstLetter(getMonthText(value, true));
                  },
                  zoom: true,
                  disableLine: true,
                },
              ]}
              yAxis={[
                {
                  valueFormatter: valueFormatterCallback,
                  zoom: true,
                  width: 100,
                },
              ]}
              zoomInteractionConfig={zoomConfig}
            >
              <ChartsReferenceLine y={0} lineStyle={{ strokeWidth: 1 }} />
            </LineChartPro>
          </EDBox>
        </EDControlledExpansionPanel>
      </EDBox>
      {props.showFordelningDiagram && (
        <EDBox
          sx={{ width: '100%' }}
          data-print-section="cirkeldiagram"
          className={`print-section ${
            !props.printSections.cirkeldiagram ? 'print-hidden' : ''
          } ${
            props.firstVisibleSection === 'cirkeldiagram' ? 'first-section' : ''
          }`}
        >
          <EDControlledExpansionPanel
            borderVisible
            expanded={props.expandedPieChart}
            headerColor={theme.customProperties.MenuList.baseBg}
            expandIconColor="primary"
            label={
              <EkonomiskAnalysExpansionLabel
                labelText={`${getPieChartLabel(props.analysType)}`}
                subLabelText={`${monthFullText} ${props.dates.from.year}`}
                iconType="pie_chart"
                iconVerticalAlign="text-bottom"
              />
            }
            onChange={(_, newValue) => props.setExpandedPieChart(newValue)}
            isCompact
          >
            <EDBox
              sx={{
                width: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <PieChart
                height={500}
                colors={[
                  theme.customProperties.dustyRainbow.glauca,
                  theme.customProperties.dustyRainbow.salmon,
                  theme.customProperties.dustyRainbow.chambray,
                  theme.customProperties.dustyRainbow.amber,
                  theme.customProperties.dustyRainbow.heather,
                  theme.customProperties.dustyRainbow.pistachio,
                  theme.customProperties.dustyRainbow.redwood,
                  theme.customProperties.dustyRainbow.oat,
                  theme.customProperties.dustyRainbow.conifer,
                  theme.customProperties.dustyRainbow.pebble,
                  theme.customProperties.dustyRainbow.twilight,
                  theme.customProperties.dustyRainbow.artichoke,
                  theme.customProperties.dustyRainbow.hyacinth,
                  theme.customProperties.dustyRainbow.blush,
                  theme.customProperties.dustyRainbow.lagoon,
                  theme.customProperties.dustyRainbow.walnut,
                ]}
                slotProps={{
                  tooltip: { trigger: 'none' },
                  legend: {
                    direction: 'vertical',
                    sx: {
                      overflowY: 'scroll',
                      flexWrap: 'nowrap',
                      height: '100%',
                      [`.${legendClasses.mark}`]: {
                        width: 15,
                        height: 15,
                      },
                    },
                  },
                }}
                slots={{
                  tooltip: tooltipProps => (
                    <EkonomiskAnalysCustomItemTooltip {...tooltipProps} />
                  ),
                }}
                series={[
                  {
                    data: pieChartData,
                    id: 'percentage',
                    valueFormatter: (item: { value: number }) =>
                      getPercentageText(item.value),
                    highlightScope: {
                      highlight: 'item' as HighlightOptions,
                      fade: 'global' as FadeOptions,
                    },
                    cornerRadius: 8,
                    paddingAngle: 1,
                  },
                ]}
                sx={{
                  width: '100%',
                }}
              />
            </EDBox>
          </EDControlledExpansionPanel>
        </EDBox>
      )}
      <EDBox
        sx={{ width: '100%' }}
        data-print-section="stapeldiagram"
        className={`print-section ${
          !props.printSections.stapeldiagram ? 'print-hidden' : ''
        } ${
          props.firstVisibleSection === 'stapeldiagram' ? 'first-section' : ''
        }`}
      >
        <EDControlledExpansionPanel
          borderVisible
          headerColor={theme.customProperties.MenuList.baseBg}
          expanded={props.expandedBarChart}
          expandIconColor="primary"
          label={
            <EkonomiskAnalysExpansionLabel
              labelText="Utfall mot budget"
              subLabelText={`${monthFullText} ${props.dates.to.year}`}
              iconType="chart_bar"
              iconVerticalAlign="text-top"
            />
          }
          onChange={(_, newValue) => props.setExpandedBarChart(newValue)}
          isCompact
        >
          <EDBox
            sx={{
              width: '100%',
              minWidth: 0,
              overflowX: 'hidden',
              display: 'flex',
              flexFlow: 'column',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <EDTypography
              variant="h6"
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                mb: 1,
                '@media print': {
                  display: 'none',
                },
              }}
            >
              <EDTypography fontSize={10} p={0} m={0}>
                Zoom: Alt + scroll
              </EDTypography>
            </EDTypography>
            <BarChartPro
              dataset={barChartData}
              grid={{ horizontal: true }}
              height={500}
              series={barChartSeries}
              slotProps={{
                tooltip: { trigger: 'axis' },
              }}
              slots={{
                tooltip: tooltipProps => (
                  <EkonomiskAnalysCustomAxisTooltip
                    {...tooltipProps}
                    currencyValues
                  />
                ),
              }}
              sx={{
                width: '100%',
              }}
              xAxis={[
                {
                  zoom: true,
                  scaleType: 'band',
                  tickInterval: barChartData.map(row => {
                    const headerValue = getKontodelString(row.header);

                    return headerValue;
                  }),
                  tickNumber: barChartData?.length,
                  dataKey: 'header',
                  disableLine: true,
                  valueFormatter(value, context) {
                    if (context.location === 'tick') {
                      const length = barChartData?.length;
                      const text = value?.toString() ?? '';
                      if (isSmallScreen || isMediumScreen) {
                        if (length > 15 && text.length > 3) {
                          return text.slice(0, 2);
                        }
                        if (length > 7 && text.length > 6) {
                          return text.split('-')[0] + '...';
                        }
                      }

                      if (isLargeScreen) {
                        const textLength = Math.trunc(
                          document.body.clientWidth / (length * 10)
                        );
                        if (text.length > 3 && textLength <= 3) {
                          return text.slice(0, 2);
                        }
                        if (length > 7 && text.length > textLength) {
                          return text.slice(0, textLength - 3) + '...';
                        }
                      }
                    }
                    return value;
                  },
                },
              ]}
              yAxis={[
                {
                  zoom: true,
                  valueFormatter: valueFormatterCallback,
                  width: 100,
                },
              ]}
              zoomInteractionConfig={zoomConfig}
            >
              <ChartsReferenceLine y={0} lineStyle={{ strokeWidth: 1 }} />
            </BarChartPro>
            <EDBox className="print-hidden">
              <EDRadioGroup
                marginTop="none"
                label=""
                onChange={onRadioBarChartBtnChange}
                radioDefs={radioBarChartDefs}
                value={sortByBarChartData}
                row
              />
            </EDBox>
          </EDBox>
        </EDControlledExpansionPanel>
      </EDBox>
    </EDBox>
  );
};

export default EkonomiskAnalysSammanfattningCharts;
